import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download } from 'lucide-react';
import { PRODUCTS, INDUSTRIES, FACILITY } from '../data/content';
import { MaskedRevealOnView, FadeUp } from './motion/Reveal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const slugMap = { 'BB · RND': 'rnd', 'BB · HEX': 'hex', 'BB · SQR': 'sqr', 'BB · FLT': 'flt' };

const productImages = [
    FACILITY.bars,
    FACILITY.drawBench,
    FACILITY.machines,
    FACILITY.plantWide,
];

const Products = () => {
    const [active, setActive] = useState(0);
    const p = PRODUCTS[active];

    return (
        <section id="products" data-testid="products-section" className="bg-[#0A0A0B] text-white py-28 md:py-40 border-t border-white/10 relative overflow-hidden">
            {/* Blueprint grid */}
            <div
                className="absolute inset-0 pointer-events-none opacity-[0.04]"
                style={{
                    backgroundImage:
                        'linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)',
                    backgroundSize: '80px 80px',
                }}
            />

            <div className="relative mx-auto max-w-[1500px] px-6 md:px-10">
                <div className="grid grid-cols-12 gap-6 mb-16">
                    <div className="col-span-12 md:col-span-4">
                        <span className="overline text-[#FF3B00]">Catalog · 02</span>
                    </div>
                    <div className="col-span-12 md:col-span-8">
                        <MaskedRevealOnView
                            className="h-display text-[10vw] md:text-[6vw] leading-[0.9]"
                            lines={['The bright bar', 'programme.']}
                        />
                        <FadeUp delay={0.15}>
                            <p className="mt-6 max-w-2xl text-white/70 text-base md:text-lg leading-relaxed">
                                Round, hex, square and flat — every bar drawn, straightened and finished to spec. Explore the range and download a quote in a single tap.
                            </p>
                        </FadeUp>
                    </div>
                </div>

                {/* Category selector */}
                <div className="border-t border-white/15 grid grid-cols-2 md:grid-cols-4 mb-0">
                    {PRODUCTS.map((prod, i) => (
                        <button
                            key={prod.code}
                            data-testid={`product-tab-${i}`}
                            onClick={() => setActive(i)}
                            className={`text-left px-5 py-6 border-r last:border-r-0 border-white/15 transition-colors ${
                                active === i ? 'bg-[#FF3B00] text-white' : 'hover:bg-white/5 text-white'
                            }`}
                        >
                            <div className="overline opacity-70">{prod.code}</div>
                            <div className="mt-2 h-display text-xl md:text-2xl">{prod.name}</div>
                        </button>
                    ))}
                </div>

                {/* Active product detail */}
                <div className="grid grid-cols-12 gap-6 border-t border-white/15 pt-14">
                    <div className="col-span-12 lg:col-span-5">
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={active}
                                initial={{ opacity: 0, scale: 1.04 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.98 }}
                                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                                className="relative aspect-[4/5] w-full overflow-hidden clip-corner bg-black"
                            >
                                <img src={productImages[active]} alt={p.name} className="w-full h-full object-cover" />
                                <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 50% 40%, transparent 30%, rgba(0,0,0,0.55) 80%)' }} />
                                <div className="absolute top-4 left-4 overline text-white/80">{p.code}</div>
                                <div className="absolute bottom-6 left-6 right-6">
                                    <div className="h-display text-3xl md:text-4xl">{p.name}</div>
                                </div>
                            </motion.div>
                        </AnimatePresence>
                    </div>

                    <div className="col-span-12 lg:col-span-7">
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={active}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                transition={{ duration: 0.5 }}
                            >
                                <table className="w-full text-left font-mono text-sm">
                                    <tbody className="divide-y divide-white/15">
                                        <tr className="border-t border-white/15">
                                            <th className="py-4 pr-6 w-40 overline text-white/50 font-normal align-top">Size range</th>
                                            <td className="py-4 text-white text-base md:text-lg">{p.sizeRange}</td>
                                        </tr>
                                        <tr>
                                            <th className="py-4 pr-6 overline text-white/50 font-normal align-top">Tolerance</th>
                                            <td className="py-4 text-white text-base md:text-lg">{p.tolerance}</td>
                                        </tr>
                                        <tr>
                                            <th className="py-4 pr-6 overline text-white/50 font-normal align-top">Grades</th>
                                            <td className="py-4">
                                                <div className="flex flex-wrap gap-2">
                                                    {p.grades.map((g) => (
                                                        <span key={g} className="border border-white/30 px-3 py-1.5 text-xs text-white">{g}</span>
                                                    ))}
                                                </div>
                                            </td>
                                        </tr>
                                        <tr>
                                            <th className="py-4 pr-6 overline text-white/50 font-normal align-top">Finish</th>
                                            <td className="py-4 text-white text-base md:text-lg">{p.finish}</td>
                                        </tr>
                                    </tbody>
                                </table>

                                <div className="mt-10 flex flex-wrap items-center gap-3">
                                    <a
                                        href="#contact"
                                        data-testid={`product-quote-${active}`}
                                        className="btn-invert inline-flex items-center gap-3 h-14 px-8 bg-[#FF3B00] text-white text-[11px] font-mono uppercase tracking-[0.24em] hover:text-[#FF3B00]"
                                    >
                                        Request quote for {p.code} →
                                    </a>
                                    <a
                                        href={`${API}/products/${slugMap[p.code]}/brochure`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid={`product-brochure-${active}`}
                                        className="inline-flex items-center gap-2 h-14 px-6 border border-white/30 text-white text-[11px] font-mono uppercase tracking-[0.24em] hover:border-white hover:bg-white hover:text-[#0A0A0B] transition-colors"
                                    >
                                        <Download size={14} strokeWidth={1.75} />
                                        Download brochure · PDF
                                    </a>
                                </div>
                            </motion.div>
                        </AnimatePresence>
                    </div>
                </div>

                {/* Industries served */}
                <div className="mt-24 border-t border-white/15 pt-10">
                    <div className="overline text-white/50 mb-6">Industries served</div>
                    <div className="flex flex-wrap gap-x-8 gap-y-3">
                        {INDUSTRIES.map((ind) => (
                            <span key={ind} className="h-display text-2xl md:text-4xl text-white/60 hover:text-white transition-colors">
                                {ind}<span className="text-[#FF3B00]">.</span>
                            </span>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Products;
